/* DentPDR product page elements (design.md section 7): <media-gallery>, <zoom-viewer>, <sticky-atc>,
   <fbt-picker>, <product-recommendations>, <material-drawer> and collapsible blocks.
   Loads after dp-commerce.js, which provides window.DPOverlay, DPStopMedia and DPCommerce. */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  const desktop = matchMedia('(min-width: 1024px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const define = (name, cls) => customElements.get(name) || customElements.define(name, cls);

  /* ---------- <media-gallery> ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-dp-track]', this);
      this.slides = $$('[data-dp-slide]', this);
      this.thumbs = $$('[data-dp-thumb]', this);
      this.dots = $$('[data-dp-dot]', this);
      this.counter = $('[data-dp-counter]', this);
      this.chip = $('[data-dp-variant-chip]', this);
      this.at = +this.dataset.current || 0;
      this.featuredId = this.chip && !this.chip.hidden ? this.slides[this.at].dataset.mediaId : '';
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-dp-thumb]');
        if (thumb) this.select(+thumb.dataset.index);
        // the zoom button or a click on the main photo opens the lightbox (Burak 2026-10-24)
        const opener = e.target.closest('[data-dp-zoom-open]') || e.target.closest('.dp-gallery__img');
        if (opener) {
          const zoom = $('zoom-viewer');
          if (zoom) zoom.show(this.at, $('[data-dp-zoom-open]', this) || opener);
        }
      });
      this.magnifier();
      this.autoplay();
      let frame;
      this.track.addEventListener('scroll', () => {
        if (desktop.matches) return;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const i = Math.round(Math.abs(this.track.scrollLeft) / this.track.clientWidth);
          if (i !== this.at && this.slides[i]) this.select(i, false);
        });
      }, { passive: true });
      this.off = new AbortController();
      document.addEventListener('dp:variant:change', (e) => this.follow(e.detail), { signal: this.off.signal });
      document.addEventListener('dp:zoom:closed', (e) => this.select(e.detail.index), { signal: this.off.signal });
      if (this.at > 0 && !desktop.matches) this.track.scrollTo({ left: this.at * this.track.clientWidth, behavior: 'instant' });
    }
    disconnectedCallback() {
      if (this.off) this.off.abort();
      clearInterval(this.timer);
      if (this.io) this.io.disconnect();
    }
    /* next photo every 6 s (Burak 2026-10-24); waits while hovered, focused, off screen or in a background tab,
       and stops for good once the visitor clicks, touches or changes the variant. Off with reduced motion. */
    autoplay() {
      if (this.slides.length < 2 || reduced.matches) return;
      let hold = false, seen = false;
      this.io = new IntersectionObserver(([e]) => { seen = e.isIntersecting; });
      this.io.observe(this);
      this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hold = true; });
      this.addEventListener('pointerleave', () => { hold = false; });
      const stop = () => clearInterval(this.timer);
      this.addEventListener('pointerdown', stop);
      this.addEventListener('keydown', stop);
      document.addEventListener('dp:variant:change', stop, { signal: this.off.signal });
      this.timer = setInterval(() => {
        if (hold || !seen || document.hidden || this.contains(document.activeElement)) return;
        this.select((this.at + 1) % this.slides.length);
      }, 6000);
    }
    /* desktop mouse: hovering a thumbnail shows its photo, hovering the main photo magnifies it 2.5x under the cursor */
    magnifier() {
      const fine = matchMedia('(hover: hover) and (pointer: fine)');
      this.thumbs.forEach((t) => t.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse' && desktop.matches) this.select(+t.dataset.index);
      }));
      const well = $('.dp-gallery__well', this);
      const reset = (img) => { if (img) { img.style.transform = ''; img.parentElement.removeAttribute('data-magnify'); } };
      well.addEventListener('pointermove', (e) => {
        const img = e.target.closest('.dp-gallery__img');
        if (!img || e.pointerType !== 'mouse' || !desktop.matches || !fine.matches) return reset(this.lensImg);
        if (this.lensImg !== img) { reset(this.lensImg); this.lensImg = img; }
        img.sizes = '200vw'; // loads the 2048 px file so the magnified view stays sharp
        const r = img.getBoundingClientRect();
        img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
        img.style.transform = 'scale(2.5)';
        img.parentElement.setAttribute('data-magnify', '');
      });
      well.addEventListener('pointerleave', () => reset(this.lensImg));
    }
    follow({ formId, variant }) {
      if (formId !== this.dataset.formId || !variant) return;
      this.featuredId = variant.featuredMediaId ? String(variant.featuredMediaId) : '';
      if (this.chip) this.chip.textContent = variant.title;
      const i = this.slides.findIndex((s) => s.dataset.mediaId === this.featuredId);
      if (i > -1) this.select(i); else this.paintChip();
    }
    paintChip() {
      if (this.chip) this.chip.hidden = !this.featuredId || this.slides[this.at].dataset.mediaId !== this.featuredId;
    }
    select(i, scroll = true) {
      if (window.DPStopMedia) DPStopMedia(this.slides[this.at]);
      if (this.lensImg) { this.lensImg.style.transform = ''; this.lensImg.parentElement.removeAttribute('data-magnify'); }
      this.at = i;
      this.slides.forEach((s, n) => s.toggleAttribute('data-current', n === i));
      this.thumbs.forEach((t, n) => t.setAttribute('aria-pressed', n === i));
      this.dots.forEach((d, n) => d.toggleAttribute('data-current', n === i));
      if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
      this.paintChip();
      const thumb = this.thumbs[i];
      if (thumb) {
        const box = thumb.parentElement;
        const dx = thumb.getBoundingClientRect().left - box.getBoundingClientRect().left - (box.clientWidth - thumb.clientWidth) / 2;
        box.scrollTo({ left: box.scrollLeft + dx, behavior: reduced.matches ? 'instant' : 'smooth' });
      }
      if (scroll && !desktop.matches) this.track.scrollTo({ left: i * this.track.clientWidth, behavior: reduced.matches ? 'instant' : 'smooth' });
    }
  }

  /* ---------- <zoom-viewer> (images only, D26) ---------- */
  class ZoomViewer extends DPOverlay {
    connectedCallback() {
      super.connectedCallback();
      if (this._zoom) return;
      this._zoom = true;
      this.items = JSON.parse($('[data-dp-zoom-items]', this).textContent);
      this.img = $('[data-dp-zoom-img]', this);
      this.well = $('[data-dp-zoom-well]', this);
      this.n = 0;
      this.addEventListener('click', (e) => {
        const step = e.target.closest('[data-dp-zoom-step]');
        if (step) return this.go(+step.dataset.dpZoomStep);
        if (e.target === this.img) this.toggle(e);
      });
      this.well.addEventListener('wheel', (e) => { e.preventDefault(); this.toggle(e, e.deltaY < 0); }, { passive: false });
      this.well.addEventListener('mousemove', (e) => { if (this.img.hasAttribute('data-zoomed')) this.origin(e); });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.go(1);
        if (e.key === 'ArrowLeft') this.go(-1);
      });
    }
    origin(e) {
      const r = this.img.getBoundingClientRect();
      this.img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
    }
    toggle(e, on = !this.img.hasAttribute('data-zoomed')) {
      if (on) this.origin(e);
      this.img.toggleAttribute('data-zoomed', on);
    }
    show(mediaIndex, trigger) {
      const n = this.items.findIndex((it) => it && it.index >= mediaIndex);
      this.n = n > -1 ? n : 0;
      this.paint();
      this.open({ returnTo: trigger });
    }
    go(d) {
      this.n = (this.n + d + this.items.length) % this.items.length;
      this.paint();
    }
    paint() {
      const it = this.items[this.n];
      this.img.removeAttribute('data-zoomed');
      this.img.sizes = '100vw';
      this.img.srcset = it.srcset;
      this.img.src = it.src;
      this.img.alt = it.alt;
      const tpl = matchMedia('(pointer: coarse)').matches ? this.dataset.hintTouch : this.dataset.hintDesktop;
      $('[data-dp-zoom-hint]', this).textContent = tpl.replace('__N__', this.n + 1).replace('__T__', this.items.length);
    }
    afterClose() { emit('dp:zoom:closed', { index: this.items[this.n].index }); }
  }

  /* ---------- <material-drawer> ---------- */
  class MaterialDrawer extends DPOverlay {}

  /* ---------- <sticky-atc> (below 1024 px) ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = $(this.dataset.target);
      this.off = new AbortController();
      if (target) {
        this.io = new IntersectionObserver(([entry]) => this.toggle(!entry.isIntersecting && entry.boundingClientRect.bottom < 0));
        this.io.observe(target);
      }
      desktop.addEventListener('change', () => this.toggle(false), { signal: this.off.signal });
      document.addEventListener('dp:variant:change', (e) => {
        const v = e.detail.formId === this.dataset.formId && e.detail.variant;
        if (!v) return;
        const name = $('[data-dp-sticky-variant]', this);
        if (name) name.textContent = v.title;
        const price = $('[data-dp-sticky-price]', this);
        price.textContent = v.priceFormatted;
        price.toggleAttribute('data-sale', v.compareAtPrice > v.price);
      }, { signal: this.off.signal });
    }
    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.off) this.off.abort();
    }
    toggle(show) {
      show = show && !desktop.matches;
      this.toggleAttribute('data-visible', show);
      this.setAttribute('aria-hidden', !show);
      this.inert = !show;
      document.documentElement.style.setProperty('--dp-bottom-bar', show ? this.offsetHeight + 'px' : '0px');
    }
  }

  /* ---------- <fbt-picker> ---------- */
  class FbtPicker extends HTMLElement {
    connectedCallback() {
      this.items = $$('[data-fbt-item]', this);
      this.addEventListener('change', () => this.paint());
      $('[data-fbt-add]', this).addEventListener('click', () => this.add());
      this.off = new AbortController();
      document.addEventListener('dp:variant:change', (e) => {
        const v = e.detail.variant;
        if (v && e.detail.formId.startsWith('product-form')) this.reload(v.id);
      }, { signal: this.off.signal });
      this.paint();
    }
    disconnectedCallback() {
      if (this.off) this.off.abort();
      if (this.ctrl) this.ctrl.abort();
    }
    checked() { return this.items.filter((c) => c.checked); }
    paint() {
      const on = this.checked();
      const n = on.length;
      $('[data-fbt-count]', this).textContent = n === 1 ? this.dataset.one : this.dataset.other.replace('__N__', n);
      $('[data-fbt-total]', this).textContent = DP.formatMoney(on.reduce((sum, c) => sum + +c.dataset.price, 0));
      $('[data-fbt-add]', this).disabled = n === 0;
    }
    async add() {
      const btn = $('[data-fbt-add]', this);
      btn.disabled = true;
      try { await DPCommerce.addItems(this.checked().map((c) => ({ id: +c.dataset.variantId, quantity: 1 })), 'fbt'); }
      catch (err) { DPCommerce.fail(err); }
      btn.disabled = this.checked().length === 0;
    }
    async reload(variantId) {
      if (this.ctrl) this.ctrl.abort();
      const ctrl = (this.ctrl = new AbortController());
      const section = this.closest('.dp-fbt-section');
      try {
        const res = await fetch(`${this.dataset.productUrl}?variant=${variantId}&section_id=${this.dataset.sectionId}`, { signal: ctrl.signal });
        const next = $('fbt-picker', new DOMParser().parseFromString(await res.text(), 'text/html'));
        if (next) { this.replaceWith(document.importNode(next, true)); section.hidden = false; } else section.hidden = true;
      } catch (err) { /* keep the current block */ }
    }
  }

  /* ---------- <product-recommendations> ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || $('[data-dp-prec-content]', this)) return;
      const run = () => this.load();
      if (document.readyState === 'complete') run(); else addEventListener('load', run, { once: true });
    }
    async load() {
      try {
        const res = await fetch(this.dataset.url);
        const next = res.ok && $('product-recommendations', new DOMParser().parseFromString(await res.text(), 'text/html'));
        if (!next || !$('[data-dp-prec-content]', next)) throw new Error('empty');
        this.innerHTML = next.innerHTML;
      } catch (err) { (this.closest('.shopify-section') || this).remove(); }
    }
  }

  define('media-gallery', MediaGallery);
  define('zoom-viewer', ZoomViewer);
  define('material-drawer', MaterialDrawer);
  define('sticky-atc', StickyAtc);
  define('fbt-picker', FbtPicker);
  define('product-recommendations', ProductRecommendations);

  /* ---------- collapsible blocks, material trigger, reviews link ---------- */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-dp-coll]');
    if (toggle) {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', open);
      document.getElementById(toggle.getAttribute('aria-controls')).toggleAttribute('data-open', open);
    }
    const mat = e.target.closest('[data-dp-material-open]');
    if (mat && $('material-drawer')) $('material-drawer').open({ returnTo: mat });
  });


  /* overview: "Read more" only when the clipped text is longer than its box */
  $$('[data-dp-overview]').forEach((box) => {
    const body = $('.dp-pdp__overview-body', box), btn = $('.dp-pdp__more', box);
    btn.hidden = body.scrollHeight <= body.clientHeight + 4;
    btn.addEventListener('click', () => {
      const open = !box.hasAttribute('data-open');
      box.toggleAttribute('data-open', open);
      btn.setAttribute('aria-expanded', open);
      btn.textContent = open ? btn.dataset.labelLess : btn.dataset.labelMore;
    });
  });

  const none = $('[data-dp-noreviews]');
  if (none && $('#reviews')) none.hidden = false;
})();
